if (!customElements.get('comfortflex-pack-builder')) {
  class PackBuilder extends HTMLElement {
    constructor() {
      super();
      this.packSize = Number.parseInt(this.dataset.packSize, 10);
      this.itemSingular = this.dataset.itemSingular || 'item';
      this.itemPlural = this.dataset.itemPlural || 'items';
      this.completeLabel = this.dataset.completeLabel || 'Add your pack to cart';
      this.selections = [];
      this.activeCategory = 'All';
      this.searchTerm = '';
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.choiceElements = Array.from(this.querySelectorAll('[data-pack-choice]'));
      this.options = this.choiceElements.map((element, index) => ({
        index,
        element,
        name: element.dataset.name,
        sku: element.dataset.sku || '',
        category: element.dataset.category || 'All',
        image: element.dataset.image || '',
        thumbnail: element.querySelector('.pack-builder__image')?.src || element.dataset.image || '',
        thumbnailSrcset: element.querySelector('.pack-builder__image')?.srcset || '',
        alt: element.querySelector('.pack-builder__image')?.alt || element.dataset.name,
        count: 0,
      }));

      this.slots = Array.from(this.querySelectorAll('[data-pack-slot]'));
      this.progress = this.querySelector('[data-pack-progress]');
      this.guidance = this.querySelector('[data-pack-guidance]');
      this.clearButton = this.querySelector('[data-pack-clear]');
      this.filters = this.querySelector('[data-pack-filters]');
      this.searchInput = this.querySelector('[data-pack-search]');
      this.emptyMessage = this.querySelector('[data-pack-empty]');
      this.form = this.querySelector('[data-pack-form]');
      this.submitButton = this.querySelector('[data-pack-submit]');
      this.error = this.querySelector('[data-pack-error]');
      this.previewModal = this.querySelector('[data-pack-preview-modal]');
      this.previewImage = this.querySelector('[data-pack-preview-image]');
      this.previewName = this.querySelector('[data-pack-preview-name]');

      this.choiceElements.forEach((element, index) => {
        element.querySelector('[data-pack-plus]').addEventListener('click', () => this.add(index));
        element.querySelector('[data-pack-minus]').addEventListener('click', () => this.remove(index));
      });

      this.clearButton?.addEventListener('click', () => this.clear());
      this.searchInput?.addEventListener('input', (event) => {
        this.searchTerm = event.target.value.trim().toLowerCase();
        this.applyFilters();
      });
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));

      if (this.itemSingular === 'card') this.setupCardPreviews();

      this.buildFilters();
      this.render();
    }

    setupCardPreviews() {
      this.choiceElements.forEach((element, index) => {
        const trigger = element.querySelector('[data-pack-preview-open]');
        trigger?.addEventListener('click', () => this.openCardPreview(index, trigger));
      });

      this.querySelectorAll('[data-pack-preview-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeCardPreview());
      });

      this.previewModal?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeCardPreview();
        if (event.key !== 'Tab') return;
        const focusable = Array.from(this.previewModal.querySelectorAll('button:not([disabled])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    }

    openCardPreview(index, trigger) {
      if (!this.previewModal) return;
      const option = this.options[index];
      this.previewReturnFocus = trigger;
      this.previewImage.src = option.image;
      this.previewImage.alt = `Larger preview of ${option.name}`;
      this.previewName.textContent = option.name;
      this.previewModal.hidden = false;
      this.previewModal.setAttribute('aria-hidden', 'false');
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.previewModal.querySelector('.pack-builder__preview-close')?.focus();
    }

    closeCardPreview() {
      if (!this.previewModal || this.previewModal.hidden) return;
      this.previewModal.hidden = true;
      this.previewModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = this.previousBodyOverflow || '';
      this.previewReturnFocus?.focus();
    }

    add(index) {
      if (this.selections.length >= this.packSize) return;
      const option = this.options[index];
      option.count += 1;
      this.selections.push(option);
      this.render();
    }

    remove(index) {
      const option = this.options[index];
      const selectionIndex = this.selections.map((item) => item.index).lastIndexOf(index);
      if (selectionIndex === -1) return;
      option.count -= 1;
      this.selections.splice(selectionIndex, 1);
      this.render();
    }

    removeSelection(selectionIndex) {
      const option = this.selections[selectionIndex];
      if (!option) return;
      option.count -= 1;
      this.selections.splice(selectionIndex, 1);
      this.render();
    }

    clear() {
      this.options.forEach((option) => { option.count = 0; });
      this.selections = [];
      this.render();
    }

    buildFilters() {
      if (!this.filters) return;
      const categories = [...new Set(this.options.map((option) => option.category).filter(Boolean))];
      if (categories.length < 2) {
        this.filters.hidden = true;
        return;
      }

      ['All', ...categories].forEach((category) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pack-builder__filter';
        button.textContent = category;
        button.setAttribute('aria-pressed', category === 'All' ? 'true' : 'false');
        button.addEventListener('click', () => {
          this.activeCategory = category;
          this.filters.querySelectorAll('button').forEach((filterButton) => {
            filterButton.setAttribute('aria-pressed', String(filterButton === button));
          });
          this.applyFilters();
        });
        this.filters.appendChild(button);
      });
    }

    applyFilters() {
      let visibleCount = 0;
      this.options.forEach((option) => {
        const categoryMatch = this.activeCategory === 'All' || option.category === this.activeCategory;
        const searchMatch = !this.searchTerm || `${option.name} ${option.category}`.toLowerCase().includes(this.searchTerm);
        const visible = categoryMatch && searchMatch;
        option.element.hidden = !visible;
        if (visible) visibleCount += 1;
      });
      if (this.emptyMessage) this.emptyMessage.hidden = visibleCount !== 0;
    }

    render() {
      const selectedCount = this.selections.length;
      const remaining = this.packSize - selectedCount;

      this.options.forEach((option) => {
        const countOutput = option.element.querySelector('[data-pack-quantity]');
        const countBadge = option.element.querySelector('[data-pack-count]');
        const minusButton = option.element.querySelector('[data-pack-minus]');
        const plusButton = option.element.querySelector('[data-pack-plus]');
        countOutput.textContent = option.count;
        countBadge.textContent = option.count;
        countBadge.hidden = option.count === 0;
        minusButton.disabled = option.count === 0;
        plusButton.disabled = selectedCount >= this.packSize;
        option.element.classList.toggle('is-selected', option.count > 0);
      });

      this.slots.forEach((slot, index) => {
        const selection = this.selections[index];
        slot.replaceChildren();

        const number = document.createElement('span');
        number.className = 'pack-builder__slot-number';
        number.textContent = index + 1;
        slot.appendChild(number);

        if (!selection) {
          const empty = document.createElement('span');
          empty.className = 'pack-builder__slot-empty';
          empty.textContent = `Your ${this.itemSingular}`;
          slot.appendChild(empty);
          slot.classList.remove('is-filled');
          return;
        }

        const image = document.createElement('img');
        if (selection.thumbnailSrcset) {
          image.srcset = selection.thumbnailSrcset;
          image.sizes = '80px';
        }
        image.src = selection.thumbnail;
        image.alt = '';
        image.width = 80;
        image.height = 80;
        image.loading = 'lazy';
        slot.appendChild(image);

        const name = document.createElement('span');
        name.className = 'pack-builder__slot-name';
        name.textContent = selection.name;
        slot.appendChild(name);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'pack-builder__slot-remove';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `Remove ${selection.name} from slot ${index + 1}`);
        remove.addEventListener('click', () => this.removeSelection(index));
        slot.appendChild(remove);
        slot.classList.add('is-filled');
      });

      this.progress.textContent = `${selectedCount} of ${this.packSize} selected`;
      this.guidance.textContent = remaining === 0
        ? 'Your pack is ready.'
        : `Choose ${remaining} more ${remaining === 1 ? this.itemSingular : this.itemPlural}.`;
      this.clearButton.disabled = selectedCount === 0;
      const incomplete = selectedCount !== this.packSize;
      this.submitButton.disabled = incomplete;
      this.submitButton.classList.toggle('is-disabled', incomplete);
      this.submitButton.setAttribute('aria-disabled', String(incomplete));
      this.submitButton.textContent = remaining === 0
        ? this.completeLabel
        : `Choose ${remaining} more ${remaining === 1 ? this.itemSingular : this.itemPlural}`;
      this.error.hidden = true;
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.selections.length === this.packSize) {
        const properties = {
          '_Pack Builder': 'ComfortFlex reusable pack builder v1',
        };

        this.selections.forEach((selection, index) => {
          properties[`${this.capitalize(this.itemSingular)} ${index + 1}`] = selection.sku
            ? `${selection.name} (${selection.sku})`
            : selection.name;
        });

        const variantId = Number.parseInt(this.form.querySelector('[name="id"]').value, 10);
        const originalLabel = this.submitButton.textContent;
        this.submitButton.disabled = true;
        this.submitButton.classList.add('is-loading');
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.textContent = 'Adding your pack…';

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1, properties }],
            }),
          });

          if (!response.ok) {
            const details = await response.json().catch(() => ({}));
            throw new Error(details.description || details.message || 'We could not add this pack to your cart.');
          }

          window.location.assign(this.form.dataset.cartUrl || '/cart');
        } catch (error) {
          this.error.textContent = error.message || 'We could not add this pack to your cart. Please try again.';
          this.error.hidden = false;
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('is-loading');
          this.submitButton.removeAttribute('aria-busy');
          this.submitButton.textContent = originalLabel;
        }
        return;
      }

      const remaining = this.packSize - this.selections.length;
      this.error.textContent = `Please choose ${remaining} more ${remaining === 1 ? this.itemSingular : this.itemPlural}.`;
      this.error.hidden = false;
      this.error.focus?.();
    }

    capitalize(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }

  customElements.define('comfortflex-pack-builder', PackBuilder);
}
